import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, MapPin, Mail, Phone, Instagram, Facebook } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Vibrant Penticton', path: '/vibrant-penticton' },
    { name: 'Travel Stories', path: '/travel-stories' },
    { name: 'Walking Tours', path: '/walking-tours' },
    // { name: 'Store', path: '/store' }, // Hidden for now
    // { name: 'Blog', path: '/blog' }, // Hidden for now
    // { name: 'About', path: '/about' }, // Hidden for now
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass-effect shadow-lg py-2' : 'bg-transparent py-4'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_fifa-fan-guides/artifacts/g2v27v1h_anreal-1.png" 
                alt="AnReal Travel"
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-[#1E6FBA] ${
                    location.pathname === link.path ? 'text-[#1E6FBA] border-b-2 border-[#1E6FBA]' : 'text-[#1A1A1A]'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden glass-effect"
              data-testid="mobile-menu"
            >
              <div className="px-4 py-6 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block text-base font-medium transition-colors hover:text-[#1E6FBA] ${
                      location.pathname === link.path ? 'text-[#1E6FBA]' : 'text-[#1A1A1A]'
                    }`}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content */}
      <main className="flex-1 pt-16">{children}</main>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white mt-20" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                AnReal Travel
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed">
                Inspiring families and travelers to explore the world and the Okanagan region with ease.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/vibrant-penticton" className="block text-sm text-gray-300 hover:text-[#F28A30] transition-colors">
                  Vibrant Penticton
                </Link>
                <Link to="/travel-stories" className="block text-sm text-gray-300 hover:text-[#F28A30] transition-colors">
                  Travel Stories
                </Link>
                <Link to="/walking-tours" className="block text-sm text-gray-300 hover:text-[#F28A30] transition-colors">
                  Walking Tours
                </Link>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <div className="space-y-2">
                {/* Store, Blog hidden for now */}
                <Link to="/about" className="block text-sm text-gray-300 hover:text-[#F28A30] transition-colors">
                  About Us
                </Link>
                <Link to="/contact" className="block text-sm text-gray-300 hover:text-[#F28A30] transition-colors">
                  Contact
                </Link>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <MapPin className="w-4 h-4" />
                  <span>Penticton, BC, Canada</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <Mail className="w-4 h-4" />
                  <span>info@anrealtravel.ca</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-300">
                  <Phone className="w-4 h-4" />
                  <span>+1 (250) 486-3888</span>
                </div>
                <div className="flex space-x-4 mt-4">
                  <a 
                    href="http://instagram.com/anrealtravel/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-[#F28A30] transition-colors" 
                    data-testid="social-instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://www.facebook.com/p/AnReal-Travel-100070084666112/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-[#F28A30] transition-colors" 
                    data-testid="social-facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} AnReal Travel. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
